import React, { useEffect, useState } from 'react';
import { BulkLengthPricingPayload, LengthPricingTarget } from '../../types';
import { Modal } from './Modal';

interface SetLengthPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: BulkLengthPricingPayload) => void;
  carCount: number;
}

const targets: { value: LengthPricingTarget; label: string; hint?: string }[] = [
  {
    value: 'auto',
    label: 'Respect payment type',
    hint: 'Updates freight for all cars and collect only for collect (C) cars',
  },
  { value: 'freight', label: 'Apply to freight only' },
  { value: 'collect', label: 'Apply to collect only' },
  { value: 'both', label: 'Apply to both freight and collect' },
];

export const SetLengthPricingModal: React.FC<SetLengthPricingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carCount,
}) => {
  const [pricePerMeter, setPricePerMeter] = useState('');
  const [applyTo, setApplyTo] = useState<LengthPricingTarget>('auto');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setPricePerMeter('');
    setApplyTo('auto');
    setError(null);
  };

  const handleConfirm = () => {
    const trimmed = pricePerMeter.trim();
    const parsed = Number(trimmed);

    if (trimmed === '' || Number.isNaN(parsed) || parsed <= 0) {
      setError('Price per meter must be a positive number');
      return;
    }

    const payload: BulkLengthPricingPayload = {
      price_per_meter: parsed,
      apply_to: applyTo,
    };

    onConfirm(payload);
    resetState();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Set Price Based on Length"
      size="md"
      footer={(
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Apply Pricing
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Length-based pricing uses the car model length. Enter the line-meter rate to update {carCount} {carCount === 1 ? 'unit' : 'units'}.
        </p>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Price per meter
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={pricePerMeter}
            onChange={(event) => setPricePerMeter(event.target.value)}
            placeholder="e.g. 380"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="space-y-2">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Apply to</span>
          <div className="space-y-2">
            {targets.map(option => (
              <label key={option.value} className="flex items-start space-x-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="length-pricing-target"
                  value={option.value}
                  checked={applyTo === option.value}
                  onChange={() => setApplyTo(option.value)}
                />
                <span>
                  <span className="block">{option.label}</span>
                  {option.hint && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{option.hint}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}
      </div>
    </Modal>
  );
};

export default SetLengthPricingModal;
